import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, UserPlus, FileText, CheckCircle } from 'lucide-react'
import Logo from '../../components/common/Logo'
import Input from '../../components/common/Input'
import Button from '../../components/common/Button'
import AddressSelector from '../../components/common/AddressSelector'
import { storageService } from '../../services'
import { API_BASE_URL, API_ENDPOINTS } from '../../constants'

type RegisterStep = 1 | 2 | 3

function RegisterPage() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState<RegisterStep>(1)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    subdistrict: '',
    district: '',
    province: '',
    postalCode: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  })

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('รหัสผ่านไม่ตรงกัน')
      return
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร')
      return
    }

    setCurrentStep(2)
  }

  const handleStep2Submit = async () => {
    if (!acceptedTerms) {
      alert('กรุณายอมรับข้อกำหนดและเงื่อนไข')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      // Register user
      const userResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REGISTER}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          role: 'FARMER',
        }),
      })

      const userData = await userResponse.json()

      if (!userResponse.ok) {
        throw new Error(userData.message || 'ลงทะเบียนไม่สำเร็จ')
      }

      // Create farmer profile
      const profileResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.FARMERS.PROFILE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`,
        },
        body: JSON.stringify({
          userId: userData.user.id,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          address: formData.address,
          subdistrict: formData.subdistrict,
          district: formData.district,
          province: formData.province,
          postalCode: formData.postalCode,
        }),
      })

      if (!profileResponse.ok) {
        const profileError = await profileResponse.json()
        throw new Error(profileError.message || 'สร้างโปรไฟล์ไม่สำเร็จ')
      }

      // Save token and user data
      storageService.setAuthToken(userData.token)
      storageService.setLoggedIn(true)
      storageService.setUser(userData.user)

      // Go to success step
      setCurrentStep(3)
    } catch (err: any) {
      console.error('Register error:', err)
      setError(err.message || 'เกิดข้อผิดพลาดในการลงทะเบียน')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoToLogin = () => {
    navigate('/login')
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Text Overlay (เหมือน Login) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image - รูปโคจาก Figma */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/assets/images/login-bg.png)',
          }}
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full text-white p-12">
          {/* Logo - ตาม Figma */}
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-8 shadow-2xl p-4">
            <Logo size="md" variant="icon" showText={false} />
          </div>

          {/* Welcome Text - ตาม Figma */}
          <h1 className="text-6xl font-bold text-center mb-2 drop-shadow-2xl">
            ยินดีต้อนรับสู่
          </h1>
          <h2 className="text-5xl font-bold text-center drop-shadow-2xl">
            เสโนเวท เอไอ
          </h2>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-8 lg:p-12">
        <div className="w-full max-w-md">
          {/* Step 1: Form ข้อมูลส่วนตัว */}
          {currentStep === 1 && (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                  <UserPlus className="w-8 h-8 text-primary-800" />
                </div>
                <h1 className="text-2xl font-semibold text-text-primary mb-2">
                  กรุณากรอกข้อมูล
                </h1>
              </div>

              {/* Form */}
              <form onSubmit={handleStep1Submit} className="space-y-4">
                {/* ชื่อ - นามสกุล (2 columns) */}
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="ชื่อ"
                    type="text"
                    placeholder="ชื่อ"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                  <Input
                    label="นามสกุล"
                    type="text"
                    placeholder="นามสกุล"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </div>

                {/* ที่อยู่ */}
                <Input
                  label="ที่อยู่"
                  type="text"
                  placeholder="ที่อยู่"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  required
                />

                {/* Address Selector (จังหวัด อำเภอ ตำบล รหัสไปรษณีย์) */}
                <AddressSelector
                  province={formData.province}
                  district={formData.district}
                  subdistrict={formData.subdistrict}
                  postalCode={formData.postalCode}
                  onAddressChange={handleInputChange}
                />

                {/* เบอร์โทรศัพท์ */}
                <Input
                  label="เบอร์โทรศัพท์"
                  type="text"
                  placeholder="เบอร์โทรศัพท์"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                />

                {/* อีเมล */}
                <Input
                  label="อีเมล"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />

                {/* Error Message */}
                {error && (
                  <div className="bg-error-light border border-error rounded-lg p-3 text-sm text-error">
                    {error}
                  </div>
                )}

                {/* รหัสผ่าน */}
                <Input
                  label="รหัสผ่าน"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="รหัสผ่าน"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  icon={
                    showPassword ? (
                      <EyeOff className="h-5 w-5 text-text-tertiary" />
                    ) : (
                      <Eye className="h-5 w-5 text-text-tertiary" />
                    )
                  }
                  iconPosition="right"
                  onIconClick={() => setShowPassword(!showPassword)}
                  helperText="รหัสผ่านควรมีความยาวอย่างน้อย 6 ตัว 50 ตัวอักษร"
                  required
                />

                {/* ยืนยันรหัสผ่าน */}
                <Input
                  label="ยืนยันรหัสผ่าน"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="ยืนยันรหัสผ่าน"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  icon={
                    showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-text-tertiary" />
                    ) : (
                      <Eye className="h-5 w-5 text-text-tertiary" />
                    )
                  }
                  iconPosition="right"
                  onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  required
                />

                {/* Submit Button */}
                <Button type="submit" variant="primary" size="lg" className="w-full mt-6">
                  ต่อไป
                </Button>
              </form>
            </>
          )}

          {/* Step 2: Terms & Conditions */}
          {currentStep === 2 && (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                  <FileText className="w-8 h-8 text-primary-800" />
                </div>
                <h1 className="text-lg font-semibold text-text-primary mb-2">
                  โปรดอ่านและยอมรับข้อตกลงการใช้บริการ
                </h1>
                <p className="text-sm text-text-secondary">
                  กรุณาอ่านข้อกำหนดและเงื่อนไขก่อนดำเนินการต่อ
                </p>
              </div>

              {/* Terms Box */}
              <div className="border border-border-medium rounded-lg p-6 h-64 overflow-y-auto mb-6 text-sm text-text-secondary leading-relaxed">
                <p className="mb-4">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi a elementum justo, eu
                  porta enim. Aliquam eu augue mauris. Pellentesque sapien elit tincidunt arcu. Cras sed
                  laoreet justo. Sed finibus, eleifend tortor sit amet laoreet. Ut luctus, diam tempus
                  bibendum efficitur.
                </p>
                <p className="mb-4">
                  Fusce id sem consectetur, rhoncus erat commodo, suscipit tellus. Vestibulum ut viverra
                  tortor. Praesent pharetra felis odio, eget tincidunt velit aliquet at. Etiam mi libero,
                  egestas id cursus, eget, efficitur in dolor. Etiam turpis lorem, tempor ac odio nec,
                  tristique dignissim tellus.
                </p>
                <p>
                  Nunc vestibulum neque quis facilisis tempor. Sed ut blandit turpis. Vestibulum a erat
                  dictum. Pellentesque eros vitae, tempor sapien. Etiam in mi dictum tincidunt nisl a,
                  faucibus odio.
                </p>
              </div>

              {/* Checkbox */}
              <div className="flex items-start gap-3 mb-6">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-primary-800 border-border-medium rounded focus:ring-2 focus:ring-primary-800"
                />
                <label htmlFor="acceptTerms" className="text-sm text-text-secondary">
                  ข้อมูลข้างต้น เสโนเวท เอไอ ให้ข้อมูลส่วนตัวของกิจการของฉันเพื่อวัตถุประสงค์ตามที่ได้ระบุไว้ใน{' '}
                  <a href="#" className="text-primary-800 hover:underline">
                    เงื่อนฉันทำหน้าที่
                  </a>
                </label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-error-light border border-error rounded-lg p-3 text-sm text-error">
                  {error}
                </div>
              )}

              {/* Button */}
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={handleStep2Submit}
                disabled={!acceptedTerms || isLoading}
              >
                {isLoading ? 'กำลังลงทะเบียน...' : 'ฉันยอมรับข้อกำหนดและเงื่อนไข'}
              </Button>
            </>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && (
            <div className="text-center">
              {/* Success Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-success-light rounded-full mb-6">
                <CheckCircle className="w-12 h-12 text-success" />
              </div>

              {/* Success Message */}
              <h1 className="text-2xl font-bold text-text-primary mb-2">
                บัญชีของคุณพร้อมแล้ว!
              </h1>
              <p className="text-sm text-text-secondary mb-8">
                ยินดีต้อนรับสู่ เสโนเวท เอไอ
              </p>

              {/* Login Button */}
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={handleGoToLogin}
              >
                เข้าสู่ระบบ
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
